import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col } from 'react-bootstrap';
import { Download, CheckAll, XLg } from 'react-bootstrap-icons';

export interface ExportField {
  value: string;
  label: string;
}

export const CLIENT_EXPORT_FIELDS: ExportField[] = [
  { value: 'Id', label: 'ID' },
  { value: 'Name', label: 'Name' },
  { value: 'FreshbooksId', label: 'FreshBooks ID' },
  { value: 'EmailAddress', label: 'Email Address' },
  { value: 'AlternateEmailAddress', label: 'Alternate Email' },
  { value: 'PhoneNumber', label: 'Phone Number' },
  { value: 'AlternatePhoneNumber', label: 'Alternate Phone' },
  { value: 'BillingStreet', label: 'Billing Street' },
  { value: 'BillingCity', label: 'Billing City' },
  { value: 'BillingState', label: 'Billing State' },
  { value: 'BillingPostCode', label: 'Billing Post Code' },
  { value: 'BillingCountry', label: 'Billing Country' },
  { value: 'MonitoringListId', label: 'Monitoring List ID' },
  { value: 'IsMonitoringCustomer', label: 'Is Monitoring Customer' },
  { value: 'IgnoreCameraViolations', label: 'Ignore Camera Violations' },
  { value: 'AutoPayToNYC', label: 'Auto Pay To NYC' },
  { value: 'EligibleForTowing', label: 'Eligible For Towing' },
  { value: 'StartedMonitoringAt', label: 'Started Monitoring At' },
  { value: 'InsertedAt', label: 'Created At' },
  { value: 'UpdatedAt', label: 'Updated At' },
  { value: 'Plates', label: 'Plates' }
];

interface ExportFieldsModalProps {
  show: boolean;
  onHide: () => void;
  onExport: (selectedFields: ExportField[]) => void;
  availableFields?: ExportField[];
  title?: string;
  isLoading?: boolean;
}

const ExportFieldsModal: React.FC<ExportFieldsModalProps> = ({
  show,
  onHide,
  onExport,
  availableFields = CLIENT_EXPORT_FIELDS,
  title = 'Select Export Fields',
  isLoading = false
}) => {
  const [selectedValues, setSelectedValues] = useState<Set<string>>(
    new Set(availableFields.map((f) => f.value))
  );

  useEffect(() => {
    if (show) {
      setSelectedValues(new Set(availableFields.map((f) => f.value)));
    }
  }, [show, availableFields]);

  const toggleField = (value: string) => {
    setSelectedValues((prev) => {
      const next = new Set(prev);
      if (next.has(value)) {
        next.delete(value);
      } else {
        next.add(value);
      }
      return next;
    });
  };

  const selectAll = () => {
    setSelectedValues(new Set(availableFields.map((f) => f.value)));
  };

  const deselectAll = () => {
    setSelectedValues(new Set());
  };

  const handleExport = () => {
    const selected = availableFields.filter((f) => selectedValues.has(f.value));
    onExport(selected);
  };

  const selectedCount = selectedValues.size;
  const totalCount = availableFields.length;

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton className="border-0 pb-0">
        <Modal.Title className="fs-5">{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="pt-2">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <span className="text-muted small">
            {selectedCount} of {totalCount} fields selected
          </span>
          <div className="d-flex gap-2">
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={selectAll}
              disabled={selectedCount === totalCount}>
              <CheckAll className="me-1" />
              Select All
            </Button>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={deselectAll}
              disabled={selectedCount === 0}>
              <XLg className="me-1" />
              Clear All
            </Button>
          </div>
        </div>

        <div
          className="border rounded p-3"
          style={{ maxHeight: '400px', overflowY: 'auto', backgroundColor: '#fafafa' }}>
          <Row xs={1} sm={2} md={3} className="g-2">
            {availableFields.map((field) => (
              <Col key={field.value}>
                <div
                  onClick={() => toggleField(field.value)}
                  className={`d-flex align-items-center p-2 rounded cursor-pointer ${
                    selectedValues.has(field.value)
                      ? 'bg-primary bg-opacity-10 border border-primary'
                      : 'bg-white border'
                  }`}
                  style={{ cursor: 'pointer', transition: 'all 0.15s ease' }}>
                  <Form.Check
                    type="checkbox"
                    checked={selectedValues.has(field.value)}
                    onChange={() => toggleField(field.value)}
                    className="me-2"
                    style={{ pointerEvents: 'none' }}
                  />
                  <span
                    className={`small ${
                      selectedValues.has(field.value) ? 'fw-medium' : 'text-muted'
                    }`}>
                    {field.label}
                  </span>
                </div>
              </Col>
            ))}
          </Row>
        </div>
      </Modal.Body>
      <Modal.Footer className="border-0 pt-0">
        <Button variant="outline-secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleExport}
          disabled={selectedCount === 0 || isLoading}>
          {isLoading ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" />
              Exporting...
            </>
          ) : (
            <>
              <Download className="me-2" />
              Export {selectedCount} Fields
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ExportFieldsModal;
